import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { JacKeyIcon } from '@/components/wizard/JacKeyIcon';
import { useLanguage } from '@/i18n/LanguageContext';
import { cn } from '@/lib/utils';

export type JacKeyNudgeType = 
  | 'first_expression'
  | 'relationship_celebrant'
  | 'birthday_champion'
  | 'friendship_keeper'
  | 'consistency_star'
  | 'love_ambassador'
  | 'legacy_builder';

interface JacKeyNudgeProps {
  type: JacKeyNudgeType;
  onDismiss: () => void;
  className?: string;
}

export function JacKeyNudge({ type, onDismiss, className }: JacKeyNudgeProps) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(true);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 300); // Allow exit animation
    }, 8000);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 300);
  };

  // Get the emotionally-aligned message (no gamified language)
  const getMessage = (): string => {
    const nudges = t.jackey?.nudges;
    if (!nudges) return '';
    
    switch (type) {
      case 'first_expression':
        return nudges.firstExpression;
      case 'relationship_celebrant':
        return nudges.relationshipCelebrant;
      case 'birthday_champion':
        return nudges.birthdayChampion;
      case 'friendship_keeper':
        return nudges.friendshipKeeper;
      case 'consistency_star':
        return nudges.consistencyStar;
      case 'love_ambassador':
        return nudges.loveAmbassador;
      case 'legacy_builder':
        return nudges.legacyBuilder;
      default:
        return '';
    }
  };

  const message = getMessage();
  if (!message) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            'flex items-start gap-3 p-4 rounded-lg',
            'bg-[hsl(160,35%,55%)/0.08] border border-[hsl(160,35%,55%)/0.2]',
            'max-w-md',
            className
          )}
        >
          <JacKeyIcon size="sm" className="flex-shrink-0 mt-0.5" />
          
          <p className="text-sm text-foreground/80 leading-relaxed flex-1">
            {message}
          </p>

          <button
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-foreground/5 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
